/**
 * Purpose: Provides a single point of entry for the application to
 * obtain a new access token.
 *
 * It has no context of who is asking for a refresh, while only cares about:
 * - there is only one concurrent request
 * - if the request fails, it assumes the refresh token has expired (and we log the user out)
 */
import axios, { isAxiosError, type AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

import AuthenticationStore from '~/auth/authenticationStore';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import { getClientFromUrl } from '~/routing/utils';
import Configuration from '~/services/configuration';
import {
  redirectToDomainLogin,
  redirectToLogin,
  redirectToLoginEmbed,
} from '~/services/redirects';
import { logger } from '~/utils/logger';
import { captureMessage, toSentryTags } from '~/utils/sentry';

interface AegisRefreshResponse {
  access_token: string;
}

function getRefreshAccessToken(
  refreshToken: string,
): Promise<AxiosResponse<AegisRefreshResponse>> {
  const AS = AuthenticationStore();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${refreshToken}`,
  };
  const csrf = AS.getCsrf();
  if (csrf) {
    headers[LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN] = csrf;
  }

  return axios.post<AegisRefreshResponse>(
    `${Configuration.authEndpoint}/v1/auth/refresh`,
    {},
    { headers },
  );
}

function getRefreshAccessHttpOnlyToken(): Promise<AxiosResponse<unknown>> {
  const AS = AuthenticationStore();
  const csrf = AS.getCsrf();

  return axios.post(
    `${Configuration.authEndpoint}/v1/auth/refresh`,
    {},
    {
      headers: csrf ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: csrf } : {},
      withCredentials: true,
    },
  );
}

let refreshPromise: Promise<AxiosResponse<unknown>> | undefined;

function handleRefreshFailure(error: unknown) {
  const AS = AuthenticationStore();

  const details = {
    reason: 'refresh_token_failure',
    subdomainOnTab: getClientFromUrl(),
    subdomainInSession: AS.getSubDomainInSession(),
    hadRefreshToken: Boolean(AS.getRefreshToken()),
    hadCsrf: Boolean(AS.getCsrf()),
    status: isAxiosError(error) ? error.response?.status : undefined,
    responseBody: isAxiosError(error) ? error.response?.data : undefined,
  };
  logger.auth('Forced logout: access token refresh failed %o', details);
  captureMessage(
    'Forced logout: access token refresh failed',
    toSentryTags(details),
  );
  AS.clearAuthentication({ requiresReauth: true });
}

function handleRolodexRefreshFailure(error: unknown) {
  // Clear without `requiresReauth` so the re-auth modal does not arm and
  // block the redirect; SSO users have no in-tab re-auth path.
  const AS = AuthenticationStore();
  const subdomain = getClientFromUrl();

  const details = {
    reason: 'rolodex_refresh_failure',
    subdomainOnTab: subdomain,
    subdomainInSession: AS.getSubDomainInSession(),
    hadCsrf: Boolean(AS.getCsrf()),
    status: isAxiosError(error) ? error.response?.status : undefined,
    responseBody: isAxiosError(error) ? error.response?.data : undefined,
  };
  logger.auth('Forced logout: rolodex session refresh failed %o', details);
  captureMessage(
    'Forced logout: rolodex session refresh failed',
    toSentryTags(details),
  );
  AS.clearAuthentication();

  if (AS.isEmbed() && subdomain) {
    redirectToLoginEmbed();
  } else if (subdomain) {
    redirectToLogin(subdomain);
  } else {
    redirectToDomainLogin();
  }
}

const refreshAegisAccessToken = async () => {
  try {
    const AS = AuthenticationStore();
    const refreshToken = AS.getRefreshToken();

    if (refreshToken) {
      // when httponly flag is not enabled
      refreshPromise = refreshPromise || getRefreshAccessToken(refreshToken);
      const response =
        (await refreshPromise) as AxiosResponse<AegisRefreshResponse>;

      const accessToken = response.data.access_token;
      AS.saveAccessToken(accessToken);
    } else {
      // when httponly flag is enabled
      refreshPromise = getRefreshAccessHttpOnlyToken();
      await refreshPromise;
    }
    AS.saveJwtTokenCreationTime();
  } catch (error) {
    handleRefreshFailure(error);
  } finally {
    refreshPromise = undefined;
  }
};

const refreshRolodexSessionJwt = async () => {
  try {
    const AS = AuthenticationStore();
    const csrf = AS.getCsrf();

    await axios.post(
      `${Configuration.rolodexEndpoint}/api/v1/token/refresh`,
      {},
      {
        headers: csrf ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: csrf } : {},
        withCredentials: true,
      },
    );

    AS.saveJwtTokenCreationTime();
  } catch (error) {
    handleRolodexRefreshFailure(error);
  }
};

export const refreshAccessToken = async () => {
  const AS = AuthenticationStore();

  if (AS.isRolodexAuthed()) {
    return refreshRolodexSessionJwt();
  } else {
    return refreshAegisAccessToken();
  }
};

export const shouldRefreshAccessToken = () => {
  const AS = AuthenticationStore();
  const accessToken = AS.getAccessToken();
  const accessTokenCreationTime =
    // creation date + 15 minutes if Aegis, 5 minutes if Rolodex. The
    // `?? 0` mirrors the original JS `null + number === number` coercion
    // for the case where no creation time was ever recorded.
    (AS.getAccessTokenCreationTime() ?? 0) +
    (AS.isRolodexAuthed() ? 300000 : 900000);

  const tokenExpTimestamp = accessToken
    ? jwtDecode<{ exp: number }>(accessToken).exp * 1000
    : accessTokenCreationTime;

  // TODO: remove this check and the ternary above? Or return true?
  // It is unclear how this can happen without manually fiddling with the cookie in the browser console,
  // the addition of this IF block is to call this out explicitly without making actual changes.
  if (!tokenExpTimestamp) {
    return false;
  }

  const now = Date.now();
  const expiration = tokenExpTimestamp;

  if (now > expiration) {
    return true;
  }

  if (AS.isRolodexAuthed()) {
    // 1000 * 60 is the buffer for some users browser time being minute(s) off and
    // tokenExpTimestamp is server-issued - better renew the token sooner than later
    const oneMinuteFromNow = now + 1000 * 60;
    if (oneMinuteFromNow > expiration) {
      return true;
    }
  } else {
    // 1000 * 60 * 5 is the buffer for some users browser time being minute(s) off and
    // tokenExpTimestamp is server-issued - better renew the token sooner than later
    const fiveMinutesFromNow = now + 1000 * 60 * 5;
    if (fiveMinutesFromNow > expiration) {
      return true;
    }
  }

  return false;
};
